/**
 * "Hôm nay" — the exceptions, before the numbers.
 *
 * `PRODUCT.md` §2.1 says the executive's first question is *"Is anything wrong
 * today, and where?"* and says in as many words that the answer is "not a wall of
 * charts". So the first thing under the date is a list of the four things that can
 * be wrong on a day, each a 44px row carrying a glyph, a word and a count, and the
 * figures sit beside it rather than above it.
 *
 * ── Four rows, always four, counts included when they are zero ──────────────
 *
 * A list that only renders the non-empty exceptions moves under the reader's eye
 * every time it refreshes, and its silence is ambiguous: "no late row today" and
 * "the late row is at zero" look identical when the row is absent. The four rows are
 * fixed and the count is printed, so the page reads the same way every morning and
 * a zero is a measurement rather than a gap. A row at zero carries no wash and no
 * severity colour — `DESIGN.md` §12, *Luật Màu Có Nghĩa*: colour appears only when
 * there is something to act on.
 *
 * ── The row IS the primary action ───────────────────────────────────────────
 *
 * The direction contract's SIGNATURE: a focused row opens its detail inline, under
 * itself, keyboard first. Not a modal — `PRODUCT.md` has no protected-focus task
 * here — and not a route change, because the manager is comparing four exceptions
 * against each other and a page transition loses the comparison. Exactly one drawer
 * is open at a time; focusing another row moves it.
 *
 * ── The counts are claims about timers and rollups, not about people ────────
 *
 * Each drawer keeps the wording of the region it replaced: "no running timer" is a
 * fact about `GET /time-entries?status=running`, not about whether somebody is
 * working, and a failed timer read lists **nobody** rather than listing everybody
 * under an accusing heading.
 */

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';

import { useI18n } from '../../i18n';
import type { MessageKey } from '../../i18n/messages';
import type { AttendanceGridDay } from '../../api/types';
import { attendanceDrillDownPath } from '../../api/paths';
import { EmptyState } from '../../states/EmptyState';
import { StatusNote } from '../../states/StatusNote';
import { Icon, type IconName, type Severity } from '../../states/icons';

export type ExceptionKind = 'late' | 'absent' | 'noActivity' | 'unassigned';

/** Reading order: the loudest fact about the day first. */
export const EXCEPTION_KINDS: readonly ExceptionKind[] = [
  'late',
  'absent',
  'noActivity',
  'unassigned',
];

interface Presentation {
  readonly severity: Severity;
  readonly icon: IconName;
  readonly label: MessageKey;
  readonly body: MessageKey;
  readonly none: MessageKey;
  /** Kept from the region this drawer replaced, so its testids survive. */
  readonly testId: string;
}

const PRESENTATION: Readonly<Record<ExceptionKind, Presentation>> = {
  late: {
    severity: 'high',
    icon: 'triangle-alert',
    label: 'overview.exception.late',
    body: 'overview.exception.late.body',
    none: 'overview.exception.late.none',
    testId: 'k4-exception-late',
  },
  absent: {
    severity: 'critical',
    icon: 'octagon-alert',
    label: 'overview.exception.absent',
    body: 'overview.exception.absent.body',
    none: 'overview.exception.absent.none',
    testId: 'k4-exception-absent',
  },
  noActivity: {
    severity: 'medium',
    icon: 'circle-alert',
    label: 'overview.exception.noActivity',
    body: 'overview.exception.noActivity.body',
    none: 'overview.exception.noActivity.none',
    testId: 'k4-exception-no-activity',
  },
  unassigned: {
    severity: 'info',
    icon: 'info',
    label: 'overview.exception.unassigned',
    body: 'overview.unassigned.body',
    none: 'overview.unassigned.none',
    testId: 'k4-unassigned',
  },
};

export interface ExceptionListProps {
  readonly groups: Readonly<Record<ExceptionKind, readonly AttendanceGridDay[]>>;
  /** `false` when the running-timer read failed; the unassigned drawer says which. */
  readonly timersRead: boolean;
  readonly date: string;
  readonly departmentId: string | null;
}

export function ExceptionList({
  groups,
  timersRead,
  date,
  departmentId,
}: ExceptionListProps): ReactNode {
  const { t } = useI18n();
  // The drawer that is open when the page settles. Not "the worst one": that would
  // move the open panel under the reader between two refreshes of the same day.
  const [open, setOpen] = useState<ExceptionKind>('unassigned');

  const counted = (kind: ExceptionKind): boolean => kind !== 'unassigned' || timersRead;
  const ordered = [...EXCEPTION_KINDS].sort(
    (a, b) => (counted(b) ? groups[b].length : 0) - (counted(a) ? groups[a].length : 0),
  );

  return (
    <section
      className="wp-section wp-daysheet__exceptions"
      aria-labelledby="k4-exceptions-title"
      data-testid="k4-exceptions"
    >
      <h2 className="wp-section__title" id="k4-exceptions-title">
        {t('overview.exception.title')}
      </h2>

      {/*
        Ordered by how many people are in each, loudest first (finish review, fix 8).
        `EXCEPTION_KINDS` is still the tie-break, so a morning with four zeros reads
        exactly the way it always has and nothing moves under the reader between two
        refreshes of the same count. An uncounted row — a failed timer read — sorts
        with the zeros because it is not a claim that anybody needs help.
      */}
      <ul className="wp-daysheet__rows" data-testid="k4-exception-rows">
        {ordered.map((kind) => {
          const shown = PRESENTATION[kind];
          const people = groups[kind];
          // A failed timer read is not "nobody is unassigned", so the count is
          // withheld rather than printed as a zero (`PRODUCT.md` §4, row 7).
          const isCounted = counted(kind);
          const acting = isCounted && people.length > 0;
          return (
            <li className="wp-daysheet__item" key={kind}>
              <button
                type="button"
                className={`wp-daysheet__row ${acting ? `wp-daysheet__row--${shown.severity}` : 'wp-daysheet__row--quiet'}`}
                aria-expanded={open === kind}
                aria-controls={`${shown.testId}-drawer`}
                data-testid={`${shown.testId}-row`}
                onFocus={() => {
                  setOpen(kind);
                }}
                onClick={() => {
                  setOpen(kind);
                }}
              >
                <Icon name={shown.icon} className="wp-daysheet__glyph" />
                <span className="wp-daysheet__label">{t(shown.label)}</span>
                <span className="wp-daysheet__count wp-num">
                  {isCounted ? people.length : t('overview.exception.uncounted')}
                </span>
              </button>

              {open === kind ? (
                <ExceptionDrawer
                  id={`${shown.testId}-drawer`}
                  shown={shown}
                  people={people}
                  counted={isCounted}
                  date={date}
                  departmentId={departmentId}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function ExceptionDrawer({
  id,
  shown,
  people,
  counted,
  date,
  departmentId,
}: {
  readonly id: string;
  readonly shown: Presentation;
  readonly people: readonly AttendanceGridDay[];
  readonly counted: boolean;
  readonly date: string;
  readonly departmentId: string | null;
}): ReactNode {
  const { t } = useI18n();

  return (
    <div className="wp-daysheet__drawer" id={id} data-testid={shown.testId}>
      {counted ? (
        <p className="wp-small wp-muted">{t(shown.body)}</p>
      ) : (
        <StatusNote
          severity="info"
          title={t('overview.unassigned.unavailable')}
          testId="k4-unassigned-unavailable"
        />
      )}

      {counted && people.length === 0 ? (
        <EmptyState title={t(shown.none)} testId={`${shown.testId}-none`} />
      ) : null}

      {counted && people.length > 0 ? (
        <ul className="wp-rows" data-testid={`${shown.testId}-list`}>
          {people.map((person) => (
            <li className="wp-row" key={person.employee_id}>
              <Link
                to={attendanceDrillDownPath({
                  date,
                  departmentId: departmentId ?? undefined,
                  employeeId: person.employee_id,
                })}
                data-testid={`${shown.testId}-drill-${person.employee_id}`}
              >
                {person.employee_name}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
